<script setup lang="ts">
import { Component, computed, ref, watch, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { toast } from "@/common/utils/toast";
import store from "@/store";

import { DesktopCockpitTheme, MobileCockpitTheme } from ".";

import type {
  EvaluationType,
  EvaluationListType,
} from "@/common/types/evaluation";

import { evaluationShow } from "@/common/api/rest/evaluation/evaluation";

interface TabListType {
  [key: string]: {
    key: string;
    icon: string;
    label: string;
    component: Component;
  };
}

const { t } = useI18n();

const emit = defineEmits([
  "update-data",
  "add",
  "input-search",
  "search",
  "change-status",
]);

const props = defineProps<{
  accountId: string;
  evaluationId: string;
  evaluations: EvaluationListType[];
  tabList: TabListType;
  menuList: any;
  isEdit: boolean;
}>();

const evaluation = ref<EvaluationType | null>(null);

const listMenu = computed(() =>
  props.menuList.reduce((acc, item) => {
    const modules = store.getters.modules;

    if (
      item.key === "method" &&
      modules.find((mod) => mod.code === "METHOD_NOT")
    ) {
      console.log("modules", modules);
      return acc;
    } else {
      acc.push(item);
    }

    return acc;
  }, []),
);

function fetchData() {
  if (!props.evaluationId) return;

  evaluationShow(props.accountId, props.evaluationId)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        evaluation.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function update() {
  emit("update-data");
  fetchData();
}

watch(
  () => [props.evaluationId],
  () => fetchData(),
  { immediate: true, deep: true },
);

onMounted(() => {
  fetchData();
});
</script>

<template>
  <template v-if="!$store.getters.mobile">
    <desktop-cockpit-theme
      :is-edit="props.isEdit"
      :account-id="props.accountId"
      :evaluation-id="props.evaluationId || null"
      :evaluations="props.evaluations"
      :evaluation="evaluation"
      :tab-list="props.tabList"
      :menu-list="listMenu"
      @update-data="update()"
      @add="emit('add')"
      @input-search="emit('input-search', $event)"
      @search="emit('search', $event)"
      @change-status="emit('change-status', $event)"
    />
  </template>

  <template v-else>
    <mobile-cockpit-theme
      :is-edit="isEdit"
      :account-id="props.accountId"
      :evaluation-id="props.evaluationId || null"
      :evaluations="props.evaluations"
      :evaluation="evaluation"
      :tab-list="props.tabList"
      :menu-list="listMenu"
      @update-data="update()"
      @add="emit('add')"
      @input-search="emit('input-search', $event)"
      @search="emit('search', $event)"
    />
  </template>
</template>

<style lang="scss" scoped></style>
